import App from 'next/app'
import Head from 'next/head'
import 'core-js/features/promise/all-settled'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { Provider, useSelector } from 'react-redux'
import ThirdPartyScripts from '@/components/ThirdPartyScripts'
import AnalyticsWebViewTracking from '@/components/AnalyticsWebViewTracking'
import { reduxWrapper } from '@/store'
import { getInitialStoreSetup } from '@/store/helpers/storeHelpers'
import localizationHelpers from '@/store/helpers/localizationHelpers'
import configHelper from '@/store/helpers/configHelpers'
import useBrandClasses from '@/hooks/useBrandClasses'
import { useRootClassSync } from '@/hooks/useRootClassSync'
import { configsActions, configSelectors } from '@/store/configs'
import { useSerwistRegistration } from '@/hooks/useSerwistRegistration'
import * as messageListener from '@/utils/messageListener'
import { resolveQueryParam } from '@/utils/queryParams'
import { pubhubIndexedDb } from '@/utils/indexedDBHelper'
import { initDatadogRum, extractDatadogRumFromHeaders } from '@/utils/datadogRum'
import MainPage from './main'
import '@/styles/custom.scss'
import { customerId } from '@/utils/env'
import { customerHelper } from '@tweddle-se-team/common/constants'
import { cookieConsentActions } from '@/store/cookieConsent'
import { i18nHelper } from '@tweddle-se-team/common/helpers'
import { pageSelectors } from '@/store/page'
import { i18nL10nSelectors } from '@/store/i18nL10n'
const { getLocale } = i18nHelper
const { getDefaultAppTitleByCustomerId, CUSTOMER_IDS } = customerHelper
const DEFAULT_TITLE = getDefaultAppTitleByCustomerId(customerId)

const getRouteClassesByCustomer = {
  [CUSTOMER_IDS.TME]: () => [],
  [CUSTOMER_IDS.SUBARU]: () => [],
  [CUSTOMER_IDS.NORDICS]: (pathname, isInFrame) => [
    ...(pathname.startsWith('/admin') ? ['admin-page'] : []),
    ...(isInFrame && pathname.startsWith('/pdf/') ? ['pdf-iframe'] : [])
  ]
}

function DynamicPageTitle() {
  const pageTitle = useSelector(pageSelectors.selectTitle)
  const pageDescription = useSelector(pageSelectors.selectDescription)
  const title = pageTitle || DEFAULT_TITLE
  const description = pageDescription ? `Pub Hub - ${pageDescription}` : DEFAULT_TITLE

  return (
    <Head>
      <title>{title}</title>
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta name="description" content={description} />
    </Head>
  )
}

function RootClasses() {
  const router = useRouter()
  const brandClasses = useBrandClasses()
  const isInFrame = useSelector(configSelectors.selectIsInFrame)
  const getRouteClasses = getRouteClassesByCustomer[customerId] || getRouteClassesByCustomer[CUSTOMER_IDS.TME]
  const classes = [...brandClasses, ...getRouteClasses(router.pathname, isInFrame)]
  useRootClassSync(classes)
  return null
}

function LangAttributeSync() {
  const locale = useSelector(i18nL10nSelectors.selectLocale)

  useEffect(() => {
    if (locale && typeof document !== 'undefined') {
      document.documentElement.lang = locale
    }
  }, [locale])

  return null
}

// If any customer requires a specific initial language, add it here
// non-mentioned customers will default to browser language detection
const initialLangEnforcerMap = {
  [CUSTOMER_IDS.SUBARU]: 'en',
  [CUSTOMER_IDS.NORDICS]: 'en'
}

function MyApp(appProps) {
  const router = useRouter()
  const { query } = router
  const { language } = query
  const { store } = reduxWrapper.useWrappedStore(appProps)
  const { Component, pageProps, nonce, gaId, cookieConsentScriptUrl, cookieConsentClientKey, datadogRum } = appProps
  const [loading, setLoading] = useState(true)
  const [iframeDomain, setIFrameDomain] = useState(null)

  const getLanguage = async () => {
    const languageFromQuery = resolveQueryParam(language, 'language')
    if (languageFromQuery) return languageFromQuery

    if (!navigator.onLine) {
      if (router.pathname === '/content') {
        const id = resolveQueryParam(query.id, 'id')
        if (id) {
          try {
            const cached = await pubhubIndexedDb.publications.get(id)
            if (cached?.publicationFromApi?.language) return cached.publicationFromApi.language
          } catch (error) {
            console.error('Failed to read cached publication language:', error)
          }
        }
      }

      const savedLocale = getLocale()
      if (savedLocale) return savedLocale
    }

    if (initialLangEnforcerMap[customerId]) return initialLangEnforcerMap[customerId]
    return null
  }

  useEffect(() => {
    const phFrame = document.getElementById('ph-frame')
    if (phFrame) {
      const height = phFrame.offsetHeight
      window.parent.postMessage(['setHeight', height], '*')
      window.scrollTo(0, 0)
    }
  })

  // This needs to happen right after the initial load.
  useEffect(() => {
    messageListener.init()
    messageListener.subscribe('setDomain', domain => {
      console.log('Message Listener > setDomain useEffect', domain)
      setIFrameDomain(domain)
    })
  }, [])

  useEffect(() => {
    if (!router.isReady) return

    const setApp = async () => {
      try {
        setLoading(true)
        const isOffline = !navigator.onLine
        // Offline locale must not depend on API requests succeeding.
        if (isOffline) await localizationHelpers.initialLocaleState(store)(await getLanguage())
        await getInitialStoreSetup({ store, ctx: { query } })
        if (!isOffline) await localizationHelpers.initialLocaleState(store)(await getLanguage())

        // Load the consent plugin only after the user has set a locale.
        store.dispatch(cookieConsentActions.SET_SHOULD_LOAD(!!getLocale()))

        // Setting height when called from IFrame
        const height = document.getElementById('ph-frame').offsetHeight
        window.parent.postMessage(['setHeight', height], '*')
        // Message listener for iframe communication
        if (iframeDomain) {
          console.log('Setting iframe domain:', iframeDomain)
          configHelper.setIFrameDomain(store)(iframeDomain)
        }

        setLoading(false)
      } catch (error) {
        console.error('Error initializing app:', error)
        setLoading(false)
      }
    }

    setApp()
    // We only care about the initial load, so we can ignore the language change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady, store])

  useEffect(() => {
    if (store && iframeDomain) {
      console.log('Iframe domain changed:', iframeDomain)
      configHelper.setIFrameDomain(store)(iframeDomain)
    }
  }, [iframeDomain, store])

  useEffect(() => {
    store.dispatch(configsActions.SET_ONLINE_STATUS(navigator.onLine))

    if (!navigator.onLine && router.pathname !== '/cachedPublications' && router.pathname !== '/content') {
      router.replace('/cachedPublications')
    }
  }, [router, store])

  useEffect(() => {
    const goOffline = () => {
      if (router.pathname !== '/cachedPublications' && router.pathname !== '/content') {
        router.replace('/cachedPublications')
      }
    }
    window.addEventListener('offline', goOffline)
    return () => window.removeEventListener('offline', goOffline)
  }, [router])

  useSerwistRegistration()

  useEffect(() => {
    if (datadogRum) {
      initDatadogRum(datadogRum)
    }
  }, [datadogRum])

  return (
    <Provider store={store}>
      <div id="ph-frame">
        <Head>
          <meta httpEquiv="Content-Type" content="text/html charset=UTF-8" />
          <meta content="IE=edge,chrome=1" httpEquiv="X-UA-Compatible" />
          <meta content="width=device-width, initial-scale=1, maximum-scale=1, minimum-scale=1, user-scalable=no" />
          <meta property="og:type" content="website" />
        </Head>
        <DynamicPageTitle />
        <ThirdPartyScripts
          nonce={nonce}
          googleAnalyticsId={gaId}
          cookieConsentScriptUrl={cookieConsentScriptUrl}
          cookieConsentClientKey={cookieConsentClientKey}
        />
        <AnalyticsWebViewTracking />
        <MainPage id="ph-main-page" pageProps={pageProps} loading={loading}>
          <RootClasses />
          <LangAttributeSync />
          <Component {...pageProps} />
        </MainPage>
      </div>
    </Provider>
  )
}

// This is required to get values set in middleware.js file
// that we're only using to generate a nonce for the CSP header
// to execute inline scripts safely.
MyApp.getInitialProps = async appContext => {
  const appProps = await App.getInitialProps(appContext)
  const nonce = appContext.ctx?.req?.headers?.['x-nonce'] ?? ''
  const gaId = appContext.ctx?.req?.headers?.['x-gaid'] ?? ''
  const cookieConsentScriptUrl = appContext.ctx?.req?.headers?.['x-cookie-consent-script-url'] ?? ''
  const cookieConsentClientKey = appContext.ctx?.req?.headers?.['x-cookie-consent-client-key'] ?? ''
  const datadogRum = extractDatadogRumFromHeaders(appContext.ctx?.req?.headers)
  return { ...appProps, nonce, gaId, cookieConsentScriptUrl, cookieConsentClientKey, datadogRum }
}

export default MyApp
